import type { ReactNode } from 'react';
import { Svg } from '../../primitives/Svg.js';
import { useReducedMotion } from '../../../theme/hooks.js';
import type { AnimatedIconProps } from '../../primitives/types.js';

/** Refresh / reload — the arcs themselves "flow" by running a dashed pattern
 *  around each path (marching-ants on the circular track), so the motion
 *  reads as "data recirculating" rather than the whole glyph spinning.
 *  Arrowheads stay put — the eye follows the dashes chasing toward them.
 *  - active: continuous dash flow (1s loop)
 *  - idle + hover: CSS single-shot spin from icons.css remains available. */
export function IcnRefresh(props: AnimatedIconProps): ReactNode {
  const { active = false, ...rest } = props;
  const reduced = useReducedMotion();
  const animate = active && !reduced;
  // Upper arc path length ≈ 15.5 (chord arc + short tail line). Use a
  // dash pattern (≈ 2.58 + 2.58 = 5.17) that divides the track into exactly
  // 3 equal ticks so the seam doesn't swim as the pattern scrolls.
  const dash = '2.58 2.58';
  return (
    <Svg {...rest} iconName="refresh" data-on={active ? 'true' : 'false'}>
      <path d="M2 7a6 6 0 0 1 10 -2.5L15 7" strokeDasharray={animate ? dash : undefined}>
        {animate && (
          <animate
            attributeName="stroke-dashoffset"
            from="5.17"
            to="0"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </path>
      <path d="M15 3v4h-4" />
      <path d="M14 9a6 6 0 0 1 -10 2.5L1 9" strokeDasharray={animate ? dash : undefined}>
        {animate && (
          <animate
            attributeName="stroke-dashoffset"
            from="5.17"
            to="0"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </path>
      <path d="M1 13v-4h4" />
    </Svg>
  );
}
